import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { useChannel } from '@/context/ChannelContext'
import { useCreativeGenerator } from '@/onboarding/useCreativeGenerator'
import { usePinnedChannelUUID } from '@/onboarding/usePinnedChannelUUID'
import { bookmarkStep } from '@/api/onboarding'
import { type Creative, type GenerationResult, type GenerationStatus } from '@/api/creatives'

const STEP_LABELS = ['Анализ канала', 'Поиск референсов', 'Заголовки', 'Тексты', 'Картинки']

// 1..5 — текущий шаг pipeline. 0 — ещё не начали, 6 — финал/готово.
function deriveStep(status: GenerationStatus | 'idle', stage: string | undefined): number {
  // Сначала — точный парсинг "Шаг N/5" из stage (Pilotad шлёт его в новом формате).
  const m = stage?.match(/Шаг\s*(\d)\s*\/\s*5/i)
  if (m) return Number(m[1])
  // Иначе мапим по status enum.
  switch (status) {
    case 'analyzing_style':
    case 'analyzing_audience':
    case 'analyzing_problems':
      return 1
    case 'searching_references':
      return 2
    case 'generating_headlines':
      return 3
    case 'generating_texts':
      return 4
    case 'generating_images':
      return 5
    case 'validating':
    case 'completed':
      return 6
    default:
      return 0
  }
}

const STORAGE_KEY_PREFIX = 'tgx-creatives-result-'

function loadCachedResult(channelUUID: string): GenerationResult | null {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${channelUUID}`)
    if (!raw) {
      console.log('[creatives] no cache for', channelUUID)
      return null
    }
    const parsed = JSON.parse(raw) as GenerationResult
    console.log('[creatives] loaded cache', channelUUID, 'creatives:', parsed.creatives?.length)
    return parsed
  } catch (e) {
    console.warn('[creatives] failed to load cache', e)
    return null
  }
}

function saveCachedResult(channelUUID: string, result: GenerationResult): void {
  const key = `${STORAGE_KEY_PREFIX}${channelUUID}`
  const fullJson = JSON.stringify(result)
  const sizeMB = (fullJson.length / 1024 / 1024).toFixed(2)
  try {
    sessionStorage.setItem(key, fullJson)
    console.log('[creatives] cached full result for', channelUUID, '(' + sizeMB + ' MB)')
    return
  } catch (e) {
    console.warn(`[creatives] full cache save failed (${sizeMB} MB, likely quota):`, e)
  }
  // Fallback: убираем base64-картинки (data: URLs), сохраняем только текст
  try {
    const slim: GenerationResult = {
      ...result,
      creatives: result.creatives.map((c) => ({
        ...c,
        image_url: c.image_url && c.image_url.startsWith('data:') ? null : c.image_url,
      })),
    }
    sessionStorage.setItem(key, JSON.stringify(slim))
    console.log('[creatives] cached slim result (without base64 images) for', channelUUID)
  } catch (e2) {
    console.warn('[creatives] slim cache save also failed:', e2)
  }
}

function withClientIds(creatives: Creative[]): Creative[] {
  return creatives.map((c, i) => ({
    ...c,
    id: c.id || `gen-${i}-${Math.random().toString(36).slice(2, 6)}`,
  }))
}

export function CreativesEditorPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { channels } = useChannel()
  const channelUUID = usePinnedChannelUUID()
  const [creatives, setCreatives] = useState<Creative[]>([])
  const [cachedResult, setCachedResult] = useState<GenerationResult | null>(null)
  const [editing, setEditing] = useState<Creative | null>(null)
  const { state: gen, start } = useCreativeGenerator(channelUUID || undefined)

  const locState = location.state as { fallbackText?: string } | null
  const fallbackText = locState?.fallbackText

  const channelTitle = channels.find((c) => c.uuid === channelUUID)?.title || 'канал'

  // На маунте: если в sessionStorage есть свежий результат для этого канала — используем его,
  // никакой авто-генерации. Это переживает рефреш страницы.
  useEffect(() => {
    if (!channelUUID) return
    const cached = loadCachedResult(channelUUID)
    if (cached) {
      setCachedResult(cached)
      setCreatives(withClientIds(cached.creatives))
      return
    }
    if (gen.status !== 'idle') return
    start({ channelUUID, fallbackText })
  }, [channelUUID, fallbackText, gen.status, start])

  // На приход свежего gen.result — пишем в кеш, обновляем список креативов.
  useEffect(() => {
    if (!gen.result || !channelUUID) return
    saveCachedResult(channelUUID, gen.result)
    setCachedResult(gen.result)
    setCreatives(withClientIds(gen.result.creatives))
  }, [gen.result, channelUUID])

  // Эффективный result — из кеша или из текущей генерации (для analysis-карточки).
  const effectiveResult = cachedResult || gen.result

  const isLoading =
    !cachedResult && gen.status !== 'idle' && gen.status !== 'completed' && gen.status !== 'failed'

  function handleDelete(id: string) {
    if (creatives.length <= 1) return
    setCreatives((prev) => prev.filter((c) => c.id !== id))
  }

  function handleAddCreative() {
    const newCreative: Creative = {
      id: `local-${Date.now()}`,
      headline: '',
      text: '',
      image_url: null,
      segment: 'Свой креатив',
    }
    setEditing(newCreative)
  }

  function handleEditSave(updated: Creative) {
    setCreatives((prev) => {
      const exists = prev.find((c) => c.id === updated.id)
      if (exists) return prev.map((c) => (c.id === updated.id ? updated : c))
      return [...prev, updated]
    })
    setEditing(null)
  }

  function handleNext() {
    bookmarkStep('campaigns-ready')
    navigate('/onboarding/campaigns-ready', { state: { creatives } })
  }

  return (
    <OnboardingShell
      step="creatives-editor"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate('/onboarding/ad-gen-prepare')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <span className="foot-meta">
            <b>{creatives.length}</b>&nbsp;{pluralCreatives(creatives.length)}&nbsp;готовы
          </span>
          <button className="foot-next" onClick={handleNext} disabled={isLoading || creatives.length === 0}>
            Собрать кампанию
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      }
    >
      <p className="stage-eyebrow">
        <span className="stage-eyebrow-pill">Реклама · креативы</span>
        AI‑черновики готовы
      </p>
      <h1 className="stage-title">
        Готовые <em>креативы</em>&nbsp;— на старт.
      </h1>
      <p className="stage-sub">
        {creatives.length > 0
          ? `${creatives.length} ${pluralCreatives(creatives.length)} под аудиторию вашего канала.`
          : 'Креативы готовятся под аудиторию вашего канала.'}{' '}
        <b>Можно править, удалять или добавлять свои</b>. Минимум один креатив должен остаться.
      </p>

      {effectiveResult?.analysis && (
        <div className="analysis-card">
          <div className="analysis-eyebrow">Что мы поняли о канале</div>
          <p className="analysis-text">{effectiveResult.analysis.about_channel}</p>
          {effectiveResult.analysis.target_audience && (
            <div className="analysis-meta">
              <span className="analysis-meta-label">Аудитория:</span> {effectiveResult.analysis.target_audience}
            </div>
          )}
        </div>
      )}

      {isLoading && (() => {
        const currentStep = deriveStep(gen.status, gen.stage)
        return (
          <div className="gen-progress-card">
            <div className="gen-progress-header">
              <div className="spinner-large" />
              <div>
                <div className="gen-progress-title">{gen.stage || 'Генерация…'}</div>
                <div className="gen-progress-sub">Это занимает 1–2 минуты. Не закрывайте вкладку.</div>
              </div>
            </div>
            <div className="gen-steps">
              {STEP_LABELS.map((label, i) => {
                const stepNo = i + 1
                const cls =
                  stepNo < currentStep ? 'done' :
                  stepNo === currentStep ? 'active' : ''
                return (
                  <div key={label} className={`gen-step ${cls}`}>
                    <div className="gen-step-num">
                      {stepNo < currentStep ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      ) : (
                        stepNo
                      )}
                    </div>
                    <div className="gen-step-label">{label}</div>
                  </div>
                )
              })}
            </div>
            <div className="gen-progress-bar">
              <div className="gen-progress-fill" style={{ width: `${gen.progress}%` }} />
            </div>
          </div>
        )
      })()}

      {gen.status === 'failed' && gen.errorDetails?.code === 'INSUFFICIENT_POSTS' ? (
        <div className="gen-fail-card">
          <div className="gen-fail-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16v.01" />
            </svg>
          </div>
          <div className="gen-fail-content">
            <div className="gen-fail-title">В канале «{channelTitle}» мало постов для AI-анализа</div>
            <div className="gen-fail-text">
              Нашли <b>{gen.errorDetails.posts_found ?? 0}</b> валидных, нужно минимум <b>{gen.errorDetails.posts_required ?? 3}</b>. Опишите аудиторию вручную&nbsp;— сгенерируем креативы по описанию.
            </div>
            <button className="gen-fail-cta" onClick={() => navigate('/onboarding/ad-gen-prepare', { state: { openFallback: true } })}>
              Описать вручную →
            </button>
          </div>
        </div>
      ) : gen.status === 'failed' ? (
        <div className="onb-error-inline">
          <div className="onb-error-title">Генерация креативов не&nbsp;удалась</div>
          <div className="onb-error-text">
            {gen.error || gen.errorDetails?.message || 'Сервис генерации временно недоступен'}.{' '}
            <a
              href="https://max.ru/u/f9LHodD0cOJxyFsfUc55-mWzIyVtOnWwMlagPfClMqClINEKzphlsr_6FQY"
              target="_blank"
              rel="noopener noreferrer"
              className="onb-error-link"
            >
              Напишите в техподдержку
            </a>.
          </div>
          {gen.jobId && (
            <div className="onb-error-meta">job: {gen.jobId}</div>
          )}
          <div className="onb-error-actions">
            <button
              className="onb-error-btn"
              onClick={() => navigate('/onboarding/ad-gen-prepare', { state: { openFallback: true } })}
            >
              Описать вручную →
            </button>
            <a
              className="onb-error-btn ghost"
              href="https://max.ru/u/f9LHodD0cOJxyFsfUc55-mWzIyVtOnWwMlagPfClMqClINEKzphlsr_6FQY"
              target="_blank"
              rel="noopener noreferrer"
            >
              Связаться с поддержкой
            </a>
          </div>
        </div>
      ) : null}

      {!isLoading && creatives.length > 0 && (
        <div className="creatives-grid">
          {creatives.map((c, i) => {
            const sharedImage = creatives.find((x) => x.image_url)?.image_url || null
            return (
              <CreativeCard
                key={c.id}
                creative={c}
                fallbackImage={sharedImage}
                index={i}
                canDelete={creatives.length > 1}
                onEdit={() => setEditing(c)}
                onDelete={() => handleDelete(c.id)}
              />
            )
          })}
          <button className="creative-add" onClick={handleAddCreative}>
            <div className="creative-add-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </div>
            <div className="creative-add-text">Добавить креатив</div>
            <div className="creative-add-sub">Свой текст и картинка</div>
          </button>
        </div>
      )}

      {editing && (
        <EditCreativeModal
          creative={editing}
          onCancel={() => setEditing(null)}
          onSave={handleEditSave}
        />
      )}

      <style>{`
        .gen-progress-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          padding: 24px 28px;
          max-width: 760px;
          margin-bottom: 16px;
        }
        .gen-progress-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }
        .gen-progress-title {
          font-weight: 700;
          font-size: 15px;
          color: var(--tg-ink, #111);
          margin-bottom: 4px;
        }
        .gen-progress-sub {
          font-size: 12.5px;
          color: var(--tg-mute, #8f9eb0);
        }
        .gen-steps {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 6px;
          margin-bottom: 14px;
        }
        .gen-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          text-align: center;
        }
        .gen-step-num {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--tg-line-2, #f0f0f4);
          color: var(--tg-mute, #8f9eb0);
          font-weight: 700;
          font-size: 13px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-variant-numeric: tabular-nums;
          transition: background 200ms, color 200ms;
        }
        .gen-step-num svg { width: 14px; height: 14px; }
        .gen-step-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--tg-mute, #8f9eb0);
          line-height: 1.2;
          letter-spacing: 0.01em;
        }
        .gen-step.active .gen-step-num {
          background: var(--tg-blue, #0080ff);
          color: white;
          box-shadow: 0 0 0 4px var(--tg-blue-bg, #ebf2ff);
        }
        .gen-step.active .gen-step-label {
          color: var(--tg-ink, #111);
        }
        .gen-step.done .gen-step-num {
          background: var(--tg-up, #00a860);
          color: white;
        }
        .gen-step.done .gen-step-label {
          color: var(--tg-ink-2, #333);
        }
        @media (max-width: 540px) {
          .gen-step-label { font-size: 10px; }
          .gen-step-num { width: 24px; height: 24px; font-size: 12px; }
        }
        .gen-progress-bar {
          height: 6px;
          background: var(--tg-line-2, #f0f0f4);
          border-radius: 999px;
          overflow: hidden;
        }
        .gen-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--tg-blue, #0080ff), #5fb4ff);
          border-radius: 999px;
          transition: width 400ms ease-out;
        }
        .spinner-large {
          width: 40px;
          height: 40px;
          border: 3px solid var(--tg-line-2, #f0f0f4);
          border-top-color: var(--tg-blue, #0080ff);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          flex-shrink: 0;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .analysis-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 16px;
          max-width: 1100px;
        }
        .analysis-eyebrow {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
          margin-bottom: 6px;
        }
        .analysis-text {
          margin: 0 0 8px;
          font-size: 13.5px;
          line-height: 1.55;
          color: var(--tg-ink-2, #333);
        }
        .analysis-meta {
          font-size: 12.5px;
          color: var(--tg-mute, #8f9eb0);
          padding-top: 8px;
          border-top: 1px dashed var(--tg-line-2, #f0f0f4);
        }
        .analysis-meta-label {
          color: var(--tg-ink, #111);
          font-weight: 700;
        }

        .creatives-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
          max-width: 1100px;
        }
        @media (max-width: 640px) {
          .creatives-grid { grid-template-columns: 1fr; }
        }

        .creative-add {
          background: var(--tg-surface, #fff);
          border: 1.5px dashed var(--tg-line-3, #d0d0d8);
          border-radius: 16px;
          padding: 32px 20px;
          cursor: pointer;
          font-family: inherit;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: var(--tg-mute, #8f9eb0);
          transition: all 150ms;
          min-height: 280px;
        }
        .creative-add:hover {
          border-color: var(--tg-blue, #0080ff);
          color: var(--tg-blue-press, #0066cc);
          background: var(--tg-blue-bg, #ebf2ff);
        }
        .creative-add-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--tg-surface-2, #f5f5f9);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .creative-add:hover .creative-add-icon {
          background: white;
        }
        .creative-add-icon svg { width: 24px; height: 24px; }
        .creative-add-text { font-weight: 700; font-size: 14px; }
        .creative-add-sub { font-size: 11px; }

        .foot-meta {
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
          padding: 0 14px;
        }
        .foot-meta b { color: var(--tg-ink, #111); font-weight: 700; }
        .onb-error-inline {
          padding: 16px 20px;
          background: rgba(255, 64, 0, 0.08);
          border: 1px solid rgba(255, 64, 0, 0.3);
          border-radius: 12px;
          color: var(--tg-ink-2, #333);
          max-width: 760px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .onb-error-title {
          font-weight: 700;
          font-size: 15px;
          color: var(--tg-down, #ff4000);
        }
        .onb-error-text { font-size: 13px; line-height: 1.5; }
        .onb-error-link {
          color: var(--tg-blue, #0080ff);
          text-decoration: none;
          font-weight: 600;
        }
        .onb-error-link:hover { text-decoration: underline; }
        .onb-error-meta {
          font-size: 11px;
          font-family: var(--tg-font-mono, monospace);
          color: var(--tg-mute-2, #b4c0d0);
        }
        .onb-error-actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 4px; }
        .onb-error-btn {
          display: inline-flex; align-items: center;
          padding: 8px 14px;
          background: var(--tg-blue, #0080ff); color: white;
          border: 0; border-radius: 8px;
          font-family: inherit; font-weight: 600; font-size: 13px;
          text-decoration: none; cursor: pointer;
        }
        .onb-error-btn:hover { background: var(--tg-blue-hover, #0066cc); }
        .onb-error-btn.ghost {
          background: var(--tg-surface, #fff);
          color: var(--tg-ink-2);
          border: 1px solid var(--tg-line-2);
        }
        .onb-error-btn.ghost:hover { background: var(--tg-surface-2); color: var(--tg-ink); }
        .gen-fail-card {
          display: flex;
          gap: 16px;
          padding: 22px 28px;
          background: rgba(255, 184, 0, 0.10);
          border: 1px solid rgba(255, 184, 0, 0.3);
          border-radius: 12px;
          max-width: 760px;
        }
        .gen-fail-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: white;
          color: rgb(180, 130, 0);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .gen-fail-icon svg { width: 22px; height: 22px; }
        .gen-fail-content { flex: 1; }
        .gen-fail-title {
          font-weight: 700;
          font-size: 15px;
          color: var(--tg-ink, #111);
          margin-bottom: 6px;
        }
        .gen-fail-text {
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
          margin-bottom: 12px;
        }
        .gen-fail-text b { color: var(--tg-ink, #111); font-weight: 700; }
        .gen-fail-cta {
          padding: 10px 16px;
          background: var(--tg-blue, #0080ff);
          color: white;
          border: 0;
          border-radius: 8px;
          font-family: inherit;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
        }
        .gen-fail-cta:hover { background: var(--tg-blue-hover, #0066cc); }
      `}</style>
    </OnboardingShell>
  )
}

const GRADIENTS = [
  'linear-gradient(135deg, #FF8C42 0%, #FF4000 100%)',
  'linear-gradient(135deg, #5fb4ff 0%, #0080ff 100%)',
  'linear-gradient(135deg, #00a860 0%, #00805a 100%)',
  'linear-gradient(135deg, #FFD550 0%, #FF8C42 100%)',
  'linear-gradient(135deg, #9F60E8 0%, #5a32b4 100%)',
]

function CreativeCard({
  creative,
  fallbackImage,
  index,
  canDelete,
  onEdit,
  onDelete,
}: {
  creative: Creative
  fallbackImage: string | null
  index: number
  canDelete: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const imgSrc = creative.image_url || fallbackImage
  return (
    <article className="creative-card">
      <div
        className="creative-img"
        style={
          imgSrc
            ? {
                backgroundImage: `url(${imgSrc})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundColor: 'var(--tg-surface-2, #f5f5f9)',
              }
            : { background: GRADIENTS[index % GRADIENTS.length] }
        }
      >
        {creative.segment && <span className="creative-tag">{creative.segment}</span>}
      </div>
      <div className="creative-body">
        <h3 className="creative-title">{creative.headline || 'Без заголовка'}</h3>
        <p className="creative-text">{creative.text || 'Без описания'}</p>
      </div>
      <div className="creative-actions">
        <button className="creative-edit" onClick={onEdit}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
          </svg>
          Изменить
        </button>
        <button
          className="creative-del"
          title={canDelete ? 'Удалить' : 'Минимум 1 креатив'}
          disabled={!canDelete}
          onClick={onDelete}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          </svg>
        </button>
      </div>

      <style>{`
        .creative-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .creative-img {
          aspect-ratio: 1 / 1;
          position: relative;
          display: flex;
          align-items: flex-start;
          padding: 14px;
        }
        .creative-tag {
          padding: 4px 10px;
          background: rgba(38, 40, 45, 0.7);
          backdrop-filter: blur(8px);
          color: white;
          font-size: 10px;
          font-weight: 700;
          border-radius: 999px;
          letter-spacing: 0.04em;
        }
        .creative-body {
          padding: 16px 18px;
          flex: 1;
        }
        .creative-title {
          margin: 0 0 8px;
          font-size: 15px;
          font-weight: 700;
          color: var(--tg-ink, #111);
          line-height: 1.3;
        }
        .creative-text {
          margin: 0;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
        }
        .creative-actions {
          display: flex;
          gap: 4px;
          padding: 0 12px 12px;
        }
        .creative-edit, .creative-del {
          background: transparent;
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 8px;
          font-family: inherit;
          font-weight: 600;
          font-size: 12px;
          color: var(--tg-ink-2, #333);
          cursor: pointer;
          padding: 8px 12px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .creative-edit { flex: 1; }
        .creative-edit:hover {
          border-color: var(--tg-blue, #0080ff);
          color: var(--tg-blue-press, #0066cc);
        }
        .creative-edit svg { width: 12px; height: 12px; }
        .creative-del:hover:not(:disabled) {
          border-color: #c33;
          color: #c33;
        }
        .creative-del:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .creative-del svg { width: 14px; height: 14px; }
      `}</style>
    </article>
  )
}

function EditCreativeModal({
  creative,
  onCancel,
  onSave,
}: {
  creative: Creative
  onCancel: () => void
  onSave: (creative: Creative) => void
}) {
  const [headline, setHeadline] = useState(creative.headline)
  const [text, setText] = useState(creative.text)

  function handleSave() {
    onSave({ ...creative, headline, text })
  }

  return (
    <div className="modal-veil" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-title">Редактирование креатива</h3>

        <label className="modal-field">
          <span>Заголовок</span>
          <input
            type="text"
            className="modal-input"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="Введите заголовок"
            maxLength={56}
          />
          <span className="modal-hint">{headline.length}/56 символов</span>
        </label>

        <label className="modal-field">
          <span>Текст</span>
          <textarea
            className="modal-textarea"
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Введите описание"
            maxLength={200}
          />
          <span className="modal-hint">{text.length}/200 символов</span>
        </label>

        <div className="modal-actions">
          <button className="modal-cancel" onClick={onCancel}>
            Отмена
          </button>
          <button className="modal-save" onClick={handleSave} disabled={!headline.trim() || !text.trim()}>
            Сохранить
          </button>
        </div>
      </div>

      <style>{`
        .modal-veil {
          position: fixed;
          inset: 0;
          background: rgba(38, 40, 45, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: veil-in 200ms ease-out;
        }
        @keyframes veil-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .modal-content {
          background: white;
          border-radius: 16px;
          padding: 24px;
          width: calc(100% - 32px);
          max-width: 480px;
          box-shadow: 0 24px 60px -16px rgba(38,40,45,0.3);
          animation: popup-in 200ms ease-out;
        }
        @keyframes popup-in {
          from { transform: scale(0.96); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .modal-title {
          margin: 0 0 16px;
          font-size: 18px;
          font-weight: 800;
          color: var(--tg-ink, #111);
        }
        .modal-field {
          display: block;
          margin-bottom: 14px;
        }
        .modal-field > span:first-child {
          display: block;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
          margin-bottom: 6px;
        }
        .modal-input, .modal-textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 8px;
          font-family: inherit;
          font-size: 14px;
          color: var(--tg-ink, #111);
          line-height: 1.5;
          box-sizing: border-box;
        }
        .modal-textarea { resize: vertical; }
        .modal-input:focus, .modal-textarea:focus {
          outline: 0;
          border-color: var(--tg-blue, #0080ff);
        }
        .modal-hint {
          display: block;
          margin-top: 4px;
          font-size: 11px;
          color: var(--tg-mute, #8f9eb0);
        }
        .modal-actions {
          display: flex;
          gap: 8px;
          margin-top: 20px;
        }
        .modal-cancel, .modal-save {
          flex: 1;
          padding: 11px 16px;
          border: 0;
          border-radius: 10px;
          font-family: inherit;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
        }
        .modal-cancel {
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-ink, #111);
        }
        .modal-cancel:hover { background: var(--tg-line-2, #f0f0f4); }
        .modal-save {
          background: var(--tg-blue, #0080ff);
          color: white;
        }
        .modal-save:hover { background: var(--tg-blue-hover, #0066cc); }
        .modal-save:disabled {
          background: var(--tg-line-3, #d0d0d8);
          cursor: not-allowed;
        }
      `}</style>
    </div>
  )
}

function pluralCreatives(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'креатив'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'креатива'
  return 'креативов'
}
